// ─── Système de badges ────────────────────────────────────────────────────
// Deux familles :
//  - TIERED : un même badge progresse à travers des paliers (Bronze → Argent
//    → Or → Prismatique), adapté aux statistiques qui peuvent grimper longtemps.
//  - SIMPLE : un seul niveau, pour les accomplissements qui ne se "grindent"
//    pas vraiment (ex: posséder un album ancien).

export const TIER_COLORS = {
  Bronze:      '#CD7F32',
  Argent:      '#9CA3AF',
  Or:          '#D4AF37',
  Prismatique: 'prismatic', // rendu avec un dégradé arc-en-ciel
};

export const TIERED = [
  { id:'collection',  icon:'📚', label:'Collectionneur',  unit:'albums',
    metric:n=>n.total,
    tiers:[{name:'Bronze',target:10},{name:'Argent',target:50},{name:'Or',target:150},{name:'Prismatique',target:500}] },
  { id:'lecture',      icon:'📖', label:'Lecteur',         unit:'lus',
    metric:n=>n.lus,
    tiers:[{name:'Bronze',target:10},{name:'Argent',target:50},{name:'Or',target:150},{name:'Prismatique',target:400}] },
  { id:'critique',     icon:'⭐', label:'Critique',        unit:'albums notés',
    metric:n=>n.notes,
    tiers:[{name:'Bronze',target:10},{name:'Argent',target:30},{name:'Or',target:75},{name:'Prismatique',target:150}] },
  { id:'eclectique',   icon:'🌈', label:'Éclectique',      unit:'genres différents',
    metric:n=>n.genres,
    tiers:[{name:'Bronze',target:3},{name:'Argent',target:6},{name:'Or',target:10},{name:'Prismatique',target:15}] },
  { id:'connaisseur',  icon:'🏛️', label:'Connaisseur',     unit:'éditeurs différents',
    metric:n=>n.editeurs,
    tiers:[{name:'Bronze',target:3},{name:'Argent',target:6},{name:'Or',target:10},{name:'Prismatique',target:15}] },
  { id:'illustrateur', icon:'🎨', label:'Illustrateur',    unit:'albums avec photo',
    metric:n=>n.avecPhotos,
    tiers:[{name:'Bronze',target:5},{name:'Argent',target:20},{name:'Or',target:75},{name:'Prismatique',target:200}] },
  { id:'completioniste', icon:'✅', label:'Complétionniste', unit:'séries complètes',
    metric:n=>n.seriesCompletes,
    tiers:[{name:'Bronze',target:1},{name:'Argent',target:3},{name:'Or',target:6},{name:'Prismatique',target:10}] },
  { id:'genereux',     icon:'🤝', label:'Généreux',        unit:'prêts',
    metric:n=>n.pretes,
    tiers:[{name:'Bronze',target:1},{name:'Argent',target:3},{name:'Or',target:8}] },
  { id:'coupdecoeur',  icon:'💖', label:'Coup de cœur',    unit:'notes 5 étoiles',
    metric:n=>n.cinqEtoiles,
    tiers:[{name:'Bronze',target:1},{name:'Argent',target:5},{name:'Or',target:15}] },
];

export const SIMPLE = [
  { id:'premier',      icon:'🎬', label:"Et c'est parti !",  desc:'Ajouter ton premier album à la collection',                                   metric:n=>n.total,        target:1, unit:'album' },
  { id:'vintage',      icon:'🕰️', label:'Chasseur de pépites', desc:"Posséder un album publié avant 1990 (renseigne sa date de parution)",         metric:n=>n.vintage,      target:1, unit:'pépite' },
  { id:'polyglotte',   icon:'🌍', label:'Polyglotte',         desc:'Posséder des albums dans au moins 3 langues différentes',                      metric:n=>n.langues,      target:3, unit:'langues' },
  { id:'loyal',        icon:'🕯️', label:'Toujours là',        desc:'Utiliser Bulléthèque depuis plus d\'un an',                                    metric:n=>n.ancienneteJours, target:365, unit:'jours' },
  { id:'perfection',   icon:'💯', label:'Perfectionniste',    desc:'Donner une note à absolument tous tes albums (10 albums minimum)',             metric:n=>(n.notes===n.total && n.total>=10)?1:0, target:1, unit:'collection notée' },
  { id:'marathon',     icon:'🏃', label:'Marathon',           desc:'Lire 10 albums ou plus de la même série',                                      metric:n=>n.maxSerieLus,  target:10, unit:'tomes lus (1 série)' },
  { id:'multiphoto',   icon:'📷', label:'Photographe en herbe', desc:'Ajouter au moins 3 photos à un même album',                                  metric:n=>n.maxPhotos,    target:3, unit:'photos (1 album)' },
];

export function computeBadgeStats(bds, totals = {}) {
  const total = bds.length;
  const lus = bds.filter(b => b.lu).length;
  const avecPhotos = bds.filter(b => b.photos?.length > 0).length;
  const genres   = new Set(bds.map(b => b.genre).filter(Boolean)).size;
  const editeurs  = new Set(bds.map(b => b.editeur).filter(Boolean)).size;
  const langues   = new Set(bds.map(b => b.langue).filter(Boolean)).size;
  const notes     = bds.filter(b => b.note > 0).length;
  const cinqEtoiles = bds.filter(b => b.note === 5).length;
  const pretes    = bds.filter(b => b.statut === 'pret').length;
  const vintage   = bds.filter(b => { const y = parseInt(b.dateParution); return !isNaN(y) && y < 1990; }).length;
  const maxPhotos = bds.reduce((m,b) => Math.max(m, b.photos?.length||0), 0);

  const bySerie = {};
  bds.forEach(b => { const k = b.serie?.trim(); if (k) (bySerie[k] = bySerie[k] || []).push(b); });

  let seriesCompletes = 0, maxSerieLus = 0;
  Object.entries(bySerie).forEach(([serie, list]) => {
    const lusCount = list.filter(b=>b.lu).length;
    if (lusCount > maxSerieLus) maxSerieLus = lusCount;
    const t = totals[serie];
    if (!t) return;
    const owned = new Set(list.map(b => parseInt(b.numero)).filter(n => !isNaN(n)));
    let complete = true;
    for (let i = 1; i <= t; i++) if (!owned.has(i)) { complete = false; break; }
    if (complete) seriesCompletes++;
  });

  const createdDates = bds
    .map(b => b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt instanceof Date ? b.createdAt : null))
    .filter(Boolean);
  const ancienneteJours = createdDates.length
    ? Math.floor((Date.now() - Math.min(...createdDates.map(d=>d.getTime()))) / 86400000)
    : 0;

  return {
    total, lus, avecPhotos, seriesCompletes, genres, editeurs, langues,
    notes, cinqEtoiles, pretes, vintage, maxPhotos, maxSerieLus, ancienneteJours,
  };
}

// Progression d'un badge à paliers : palier courant, suivant, % vers le suivant.
export const tierProgress = (family, bds, totals = {}) => {
  const stats = computeBadgeStats(bds, totals);
  const val = family.metric(stats);
  let idx = -1;
  family.tiers.forEach((t,i) => { if (val >= t.target) idx = i; });
  const current = idx>=0 ? family.tiers[idx] : null;
  const next = family.tiers[idx+1] || null;
  return { value: val, tierIndex: idx, current, next, pct: next ? Math.min(val/next.target,1) : 1, maxed: !next };
};

// Progression d'un badge simple (un seul niveau).
export const simpleProgress = (badge, bds, totals = {}) => {
  const stats = computeBadgeStats(bds, totals);
  const current = Math.min(badge.metric(stats), badge.target);
  return { current, target: badge.target, pct: Math.min(current/badge.target,1) };
};

// Identifiants "obtenus" — un id par palier franchi (ex: 'collection:Argent')
// plus un id par badge simple débloqué. Sert à détecter les nouveautés.
export const earnedBadgeIds = (bds, totals = {}) => {
  const stats = computeBadgeStats(bds, totals);
  const ids = [];
  TIERED.forEach(fam => fam.tiers.forEach(t => { if (fam.metric(stats) >= t.target) ids.push(`${fam.id}:${t.name}`); }));
  SIMPLE.forEach(b => { if (b.metric(stats) >= b.target) ids.push(b.id); });
  return ids;
};

// Reconstruit icône/label/couleur à partir d'un id (pour toasts et animations).
export const describeBadgeId = (id) => {
  if (id.includes(':')) {
    const [famId, tierName] = id.split(':');
    const fam = TIERED.find(f => f.id === famId);
    if (!fam) return null;
    const tier = fam.tiers.find(t => t.name === tierName);
    if (!tier) return null;
    return { icon: fam.icon, label: fam.label, tierName: tier.name, color: TIER_COLORS[tier.name] };
  }
  const b = SIMPLE.find(x => x.id === id);
  return b ? { icon: b.icon, label: b.label, tierName: null, color: '#F04E1A' } : null;
};
